'use client';

import React from 'react';
import useSWR from 'swr';
import { search } from '@/clients/customer/restaurants';
import { SHOP_TYPES } from '@/constant/configs';
import { motion } from 'framer-motion';
import SearchCard from '../search/SearchCard';

type ShopsGridFeaturedProps = {
  lang: string;
  params?: Record<string, string>;
  sectionData?: any;
};

const fetcher = async (tagId: number, locale?: string) => {
  return search(
    {
      per_page: 10,
      order_dir: 'asc',
      order_by: 'order_index',
      restaurant_type: SHOP_TYPES.online_shop,
      tag_ids: tagId,
    },
    {
      ['Accept-Language']: locale || 'en',
    }
  ).then((res) => res?.data);
};

export default function ShopsGridFeatured({
  lang = 'de',
  sectionData,
  params,
}: ShopsGridFeaturedProps) {
  const tagId = sectionData?.tag_id_of_featured_shops;
  const shouldFetch = !!tagId;
  const {
    data: resData,
    error,
    isLoading,
  } = useSWR(
    shouldFetch ? ['featured-shops', tagId, params?.locale] : null,
    ([, tagId, locale]) => fetcher(tagId, locale),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // development purpose to disable cache
      // revalidateOnMount: true,
      // dedupingInterval: 0, // disables deduplication so it always re-fetches
    }
  );

  if (isLoading) {
    return (
      <div className="mb-[34px] mt-[23px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:mb-[60px] lg:mt-[28px] lg:grid-cols-3">
        {Array.from({ length: 9 }, (_, i) => (
          <RestaurantCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-12 grid grid-cols-4 gap-8 sm:grid-cols-6 lg:grid-cols-8 xl:grid-cols-12">
        <div className="col-span-full flex items-center justify-center py-8 text-h5 font-bold text-paragraph">
          {lang === 'de'
            ? 'Fehler beim Laden der Geschäfte'
            : 'Error loading stores'}
        </div>
      </div>
    );
  }

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  return (
    // Prev classes > mt-12
    <div className="mt-10">
      {resData?.data?.length > 0 ? (
        <motion.div
          className="scroll-container mb-[34px] mt-[23px] grid grid-cols-1 gap-[32px] sm:grid-cols-2 lg:mb-[60px] lg:mt-[28px] lg:grid-cols-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {resData?.data &&
            resData.data?.slice(0, 9).map((deliveryData: any) => {
              return (
                <SearchCard restaurant={deliveryData} key={deliveryData.id} />
              );
            })}
        </motion.div>
      ) : (
        <div className="col-span-full flex items-center justify-center py-8 text-h5 font-bold text-paragraph">
          {lang === 'de' ? 'Keine Geschäfte gefunden' : 'No stores found'}
        </div>
      )}
    </div>
  );
}

const RestaurantCardSkeleton = () => (
  <div className="h-[300px] w-full animate-pulse overflow-hidden rounded-[10px] bg-slate-200">
    <div className="h-[180px] rounded-t-[10px] bg-slate-300"></div>
    <div className="space-y-3 p-4">
      <div className="h-4 w-3/4 rounded bg-slate-300"></div>
      <div className="h-3 w-1/2 rounded bg-slate-300"></div>
      <div className="flex items-center justify-between">
        <div className="h-3 w-1/3 rounded bg-slate-300"></div>
        <div className="h-3 w-1/4 rounded bg-slate-300"></div>
      </div>
    </div>
  </div>
);
